import { useMemo } from 'react';

import type {
  ReportsCategoryMixEntry,
  ReportsCategoryRecommendations,
} from '@/lib/api/reports';

type OrderedCategory = {
  entry: ReportsCategoryMixEntry;
  key: string;
  recommendedRank?: number;
  recommendationReason?: string | null;
};

type HookResult = {
  orderedCategories: OrderedCategory[];
  metadata: {
    strategy: 'default' | 'popularity' | 'prep_speed';
    experimentKey?: string | null;
    variant?: string | null;
  } | null;
};

const isKillSwitchEnabled = (): boolean => {
  const runtimeFlag =
    typeof globalThis !== 'undefined' &&
    (globalThis as typeof globalThis & { __DISABLE_MENU_CATEGORY_ORDERING__?: boolean })
      .__DISABLE_MENU_CATEGORY_ORDERING__;

  if (runtimeFlag) {
    return true;
  }

  const envFlag =
    typeof import.meta !== 'undefined' &&
    typeof import.meta.env !== 'undefined' &&
    typeof import.meta.env.VITE_DISABLE_MENU_CATEGORY_ORDERING === 'string' &&
    ['1', 'true', 'yes'].includes(import.meta.env.VITE_DISABLE_MENU_CATEGORY_ORDERING.toLowerCase());

  return Boolean(envFlag);
};

const normalizeKey = (entry: ReportsCategoryMixEntry, fallbackIndex: number): string => {
  if (entry.category_id) {
    return entry.category_id;
  }
  if (entry.category_name) {
    return entry.category_name.toLowerCase();
  }
  return `category:${fallbackIndex}`;
};

export const useMenuCategoryOrdering = (
  categories: ReportsCategoryMixEntry[],
  recommendations?: ReportsCategoryRecommendations | null,
): HookResult => {
  return useMemo(() => {
    const killSwitch = isKillSwitchEnabled();
    const baseEntries: OrderedCategory[] = (categories ?? []).map((entry, index) => ({
      entry,
      key: normalizeKey(entry, index),
    }));

    if (
      killSwitch ||
      !recommendations ||
      !recommendations.categories ||
      recommendations.categories.length === 0
    ) {
      return {
        orderedCategories: baseEntries,
        metadata: null,
      };
    }

    const lookup = new Map<string, OrderedCategory>();
    for (const entry of baseEntries) {
      lookup.set(entry.key, entry);
    }

    const usedKeys = new Set<string>();
    const recommendedEntries: OrderedCategory[] = [];
    recommendations.categories.forEach((recommendation, index) => {
      const matchKey = recommendation.category_id
        ? recommendation.category_id
        : recommendation.category_name.toLowerCase();
      const match = lookup.get(matchKey);
      if (!match || usedKeys.has(match.key)) {
        return;
      }
      usedKeys.add(match.key);
      recommendedEntries.push({
        ...match,
        recommendedRank: index + 1,
        recommendationReason: recommendation.reason,
      });
    });

    const fallbackEntries = baseEntries.filter((entry) => !usedKeys.has(entry.key));

    return {
      orderedCategories: [...recommendedEntries, ...fallbackEntries],
      metadata: {
        strategy: recommendations.strategy,
        experimentKey: recommendations.experiment_key,
        variant: recommendations.variant,
      },
    };
  }, [categories, recommendations]);
};

export type UseMenuCategoryOrderingReturn = ReturnType<typeof useMenuCategoryOrdering>;
